import { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { useOnboarding } from '@/hooks/use-onboarding';
import {
  getPostAuthOrgPath,
  usePrivilegedStaffBypass,
} from '@/hooks/use-privileged-staff-bypass';
import { clearOnboardingState, getNextOnboardingStep } from '@/lib/onboarding';

const SPINNER = (
  <div className="flex h-screen items-center justify-center">
    <div className="text-center">
      <Loader2 className="text-muted-foreground mx-auto h-8 w-8 animate-spin" />
      <p className="text-muted-foreground mt-4 text-sm">Loading...</p>
    </div>
  </div>
);

/**
 * Grace period before concluding the user has no orgs.
 * Clerk's useOrganizationList can briefly report data=[] even for users
 * with memberships, especially right after sign-in or page load.
 */
const NO_ORG_GRACE_MS = 1_500;

/**
 * Onboarding Redirect Component
 * Handles smart redirection based on onboarding intent and user state
 */
export function OnboardingRedirect() {
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { userMemberships, isLoaded: membershipsLoaded } = useOrganizationList({
    userMemberships: {
      pageSize: 10,
    },
  });
  const { state: onboardingState } = useOnboarding();
  const { canBypass, isLoaded: claimsLoaded } = usePrivilegedStaffBypass();

  const hasOrg = !!(organization?.slug);
  const firstMembershipSlug = userMemberships?.data?.[0]?.organization?.slug;
  const hasAnyOrg = hasOrg || !!firstMembershipSlug;
  const allLoaded = orgLoaded && membershipsLoaded;

  const [graceElapsed, setGraceElapsed] = useState(false);
  const graceTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!allLoaded || hasAnyOrg) {
      if (graceTimer.current) clearTimeout(graceTimer.current);
      graceTimer.current = undefined;
      setGraceElapsed(false);
      return;
    }

    if (!graceElapsed && !graceTimer.current) {
      graceTimer.current = setTimeout(() => setGraceElapsed(true), NO_ORG_GRACE_MS);
    }

    return () => {
      if (graceTimer.current) clearTimeout(graceTimer.current);
      graceTimer.current = undefined;
    };
  }, [allLoaded, hasAnyOrg, graceElapsed]);

  if (!allLoaded || !claimsLoaded) return SPINNER;

  if (!hasAnyOrg && !graceElapsed) return SPINNER;

  // Prefer the membership slug over the active org slug — the active org
  // in Clerk can be stale (e.g. removed membership) and cause a redirect
  // loop when OrgRouteWrapper can't find it in its own memberships list.
  const resolveOrgSlug = () => firstMembershipSlug || organization?.slug;
  const orgEntryPath = (slug: string) => getPostAuthOrgPath(slug, canBypass);

  if (!onboardingState || !onboardingState.intent) {
    const slug = resolveOrgSlug();
    if (slug) return <Navigate to={orgEntryPath(slug)} replace />;
    return <Navigate to="/no-organization" replace />;
  }

  const slug = resolveOrgSlug();

  // Compute nextStep from the same hasAnyOrg source OnboardingRedirect uses
  // — useOnboarding's internal hooks can briefly disagree (different pageSize,
  // own loading clock) and have returned `create_org` for users that already
  // had a slug, sending them in a loop to /sign-up-new?mode=sso.
  const nextStep = getNextOnboardingStep({
    hasOrganization: hasAnyOrg,
    hasStore: false,
    state: onboardingState,
  });

  // Safety net: if the user already has an org slug, the onboarding intent is
  // stale (planted by an earlier marketplace click, never cleared). Drop it
  // and route to the org — never bounce a signed-in user back to /sign-up-new.
  if (slug && nextStep === 'create_org') {
    clearOnboardingState();
    return <Navigate to={orgEntryPath(slug)} replace />;
  }

  switch (nextStep) {
    case 'create_org':
      return <Navigate to="/sign-up-new?mode=sso" replace />;

    case 'create_store':
      if (slug) return <Navigate to={orgEntryPath(slug)} replace />;
      return <Navigate to="/sign-up-new?mode=sso" replace />;

    case 'install_plugin':
    case 'create_funnel':
    default:
      if (slug) return <Navigate to={orgEntryPath(slug)} replace />;
      return <Navigate to="/no-organization" replace />;
  }
}
